import { useState, useMemo } from 'react';
import {
  ThreadPrimitive,
  MessagePrimitive,
  ActionBarPrimitive,
  useMessage,
} from '@assistant-ui/react';
import { useIvyChatStore } from '@/stores/ivyChatStore';
import { IvyMarkdownContent } from './IvyMarkdownContent';
import { IvyComposer } from './IvyComposer';
import { IvyToolFallback } from './IvyToolFallback';
import { OutOfCreditsCard } from '@/components/billing/OutOfCreditsCard';
import { parseOutOfCreditsPart } from '@/services/billingPaywallDetector';
import { IvyWelcome } from './IvyWelcome';
import { ChatErrorNotice } from './ChatNotices';
import { parseErrorPart } from '@/lib/ivyChat/chatNotices';
import { stepsDoneLabel } from '@/lib/ivyChat/stepWords';
import { formatRelativeTime } from '@/lib/utils';
import {
  Copy, Check, RefreshCw, Wand2,
  ChevronRight, Zap,
} from 'lucide-react';

export function IvyThread() {
  return (
    <ThreadPrimitive.Root className="flex flex-col h-full min-h-0">
      <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto custom-scrollbar">
        <ThreadPrimitive.Empty>
          <IvyWelcome />
        </ThreadPrimitive.Empty>

        <div className="max-w-3xl mx-auto px-4 py-6">
          <ThreadPrimitive.Messages
            components={{
              UserMessage: IvyUserMessage,
              AssistantMessage: IvyAssistantMessage,
            }}
          />
          <StreamingIndicator />
        </div>

        <ThreadPrimitive.ViewportFooter className="sticky bottom-0">
          <ThreadScrollButton />
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>

      <IvyComposer />
    </ThreadPrimitive.Root>
  );
}

function IvyMessageTimestamp() {
  const { createdAt } = useMessage();
  if (!createdAt) return null;
  return (
    <span
      className="text-[10px] text-muted-foreground/40 select-none"
      title={createdAt.toLocaleString()}
    >
      {formatRelativeTime(createdAt)}
    </span>
  );
}

function IvyUserMessage() {
  return (
    <MessagePrimitive.Root className="flex justify-end py-2 animate-fade-in hover-reveal-parent group">
      <div className="flex items-start gap-2.5 max-w-[85%]">
        <div className="flex-1 min-w-0">
          <div className="flex justify-end mb-0.5 hover-reveal">
            <UserActionBar />
          </div>
          <div className="px-4 py-2.5 rounded-2xl bg-primary text-primary-foreground text-sm">
            <MessagePrimitive.Content
              components={{
                Text: ({ text }) => <span className="whitespace-pre-wrap">{text}</span>,
              }}
            />
          </div>
          <div className="flex justify-end mt-0.5 pr-1">
            <IvyMessageTimestamp />
          </div>
        </div>
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-muted-foreground select-none">U</span>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

function IvyAssistantMessage() {
  return (
    <MessagePrimitive.Root className="flex py-2 animate-fade-in hover-reveal-parent group">
      <div className="flex items-start gap-2.5 w-full max-w-[95%]">
        <div className="w-7 h-7 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Wand2 className="w-3.5 h-3.5 text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <GroupedContentRenderer />
          <div className="mt-0.5 flex items-center gap-2 hover-reveal">
            <IvyMessageTimestamp />
            <AssistantActionBar />
          </div>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

type Segment =
  | { type: 'text'; text: string }
  | { type: 'steps'; parts: any[] };

function GroupedContentRenderer() {
  const messageState = useMessage();
  const isRunning = messageState.status?.type === 'running';
  const checkpoint = { active: !!messageState.isLast && !isRunning, stale: !messageState.isLast };
  const content = (messageState as any).content || [];

  const segments = useMemo(() => {
    const result: Segment[] = [];
    let currentSteps: any[] = [];

    const flushSteps = () => {
      if (currentSteps.length > 0) {
        result.push({ type: 'steps', parts: currentSteps });
        currentSteps = [];
      }
    };

    for (const part of content) {
      if (part.type === 'text') {
        const text = part.text || '';
        if (text.trim()) {
          flushSteps();
          result.push({ type: 'text', text });
        }
      } else if (part.type === 'reasoning' || part.type === 'tool-call') {
        currentSteps.push(part);
      }
    }
    flushSteps();

    return result;
  }, [content]);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return <AssistantText key={`t-${i}`} text={seg.text} checkpoint={checkpoint} />;
        }
        return (
          <StepsBlock
            key={`s-${i}`}
            parts={seg.parts}
            isRunning={isRunning && i === segments.length - 1}
          />
        );
      })}
    </>
  );
}

function StepsBlock({ parts, isRunning }: { parts: any[]; isRunning: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const toolCount = parts.filter(p => p.type === 'tool-call').length;
  const thinkCount = parts.filter(p => p.type === 'reasoning').length;

  let label: string;
  if (isRunning) {
    if (toolCount > 0) {
      label = `${toolCount} step${toolCount !== 1 ? 's' : ''} running...`;
    } else {
      label = 'Thinking...';
    }
  } else if (toolCount > 0) {
    label = stepsDoneLabel(toolCount, parts.filter(p => p.type === 'tool-call' && p.isError).length);
  } else {
    label = `Thought ${thinkCount} time${thinkCount !== 1 ? 's' : ''}`;
  }

  return (
    <div className="my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-muted/30 transition-colors text-left"
      >
        <Zap className={`w-3 h-3 flex-shrink-0 ${isRunning ? 'text-info' : 'text-muted-foreground/40'}`} />
        <span className="text-xs text-muted-foreground/60">
          {label}
        </span>
        {isRunning && (
          <div className="flex gap-0.5 ml-0.5">
            <div className="w-1 h-1 rounded-full bg-info/60 typing-dot" />
            <div className="w-1 h-1 rounded-full bg-info/60 typing-dot" />
            <div className="w-1 h-1 rounded-full bg-info/60 typing-dot" />
          </div>
        )}
        <ChevronRight className={`w-3 h-3 text-muted-foreground/30 transition-transform ml-auto ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="ml-3 mt-0.5 pl-2.5 border-l border-border-secondary space-y-px">
          {parts.map((part, i) => {
            if (part.type === 'reasoning') {
              return <ReasoningItem key={`r-${i}`} text={part.text || ''} />;
            }
            if (part.type === 'tool-call') {
              return (
                <div key={part.toolCallId || `tc-${i}`} className="py-px">
                  <IvyToolFallback
                    toolName={part.toolName || 'unknown'}
                    args={part.args || {}}
                    result={part.result}
                    argsText={part.argsText}
                    status={part.status || { type: 'complete' }}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

function ReasoningItem({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="py-px">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs text-muted-foreground/40 hover:text-muted-foreground/60 hover:bg-muted/20 transition-colors w-full text-left"
      >
        <ChevronRight className={`w-2.5 h-2.5 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
        <span className="italic truncate">{text.slice(0, 70)}</span>
      </button>
      {expanded && (
        <div className="ml-4 mt-0.5 px-2 py-1 rounded bg-muted/10">
          <pre className="text-[11px] text-muted-foreground/40 italic whitespace-pre-wrap font-sans leading-relaxed max-h-32 overflow-y-auto custom-scrollbar">
            {text}
          </pre>
        </div>
      )}
    </div>
  );
}

function AssistantText({ text, checkpoint }: { text: string; checkpoint: { active: boolean; stale: boolean } }) {
  if (!text.trim()) return null;
  const paywall = parseOutOfCreditsPart(text);
  if (paywall) return <OutOfCreditsCard paywall={paywall} />;
  const error = parseErrorPart(text);
  if (error !== null) return <ChatErrorNotice message={error} />;
  if (text.startsWith('---\n') && text.includes('\u00b7')) {
    const meta = text.replace('---\n', '');
    return (
      <div className="flex items-center gap-2 py-0.5 text-[11px] text-muted-foreground/50">
        <div className="h-px flex-1 bg-border-secondary" />
        <span>{meta}</span>
        <div className="h-px flex-1 bg-border-secondary" />
      </div>
    );
  }
  return (
    <div className="prose-chat">
      <IvyMarkdownContent content={text} checkpoint={checkpoint} />
    </div>
  );
}

function StreamingIndicator() {
  const isStreaming = useIvyChatStore(s => s.isStreaming);
  const messages = useIvyChatStore(s => s.messages);
  if (!isStreaming) return null;

  // If the last message is a tool call or result, StepsBlock already shows running state.
  // Also hide for result events — turn is complete, stream close may be slightly delayed.
  const last = messages[messages.length - 1];
  if (last?.type === 'tool_use' || last?.type === 'tool_result' || last?.type === 'result') return null;

  const label = last?.type === 'thinking' ? 'Reasoning...' : 'Thinking...';

  return (
    <div className="flex items-center gap-2 py-2 pl-[38px] animate-fade-in">
      <div className="flex gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 typing-dot" />
        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 typing-dot" />
        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 typing-dot" />
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function UserActionBar() {
  return (
    <ActionBarPrimitive.Root className="flex items-center gap-1">
      <CopyButton />
    </ActionBarPrimitive.Root>
  );
}

function AssistantActionBar() {
  return (
    <ActionBarPrimitive.Root className="flex items-center gap-1">
      <CopyButton />
      <ActionBarPrimitive.Reload asChild>
        <button className="p-1 rounded-md text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
}

function CopyButton() {
  const [copied, setCopied] = useState(false);
  return (
    <ActionBarPrimitive.Copy
      onClick={() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      asChild
    >
      <button className="p-1 rounded-md text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors">
        {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </ActionBarPrimitive.Copy>
  );
}

function ThreadScrollButton() {
  return (
    <div className="flex justify-center pb-2">
      <ThreadPrimitive.ScrollToBottom asChild>
        <button className="px-3 py-1.5 rounded-full bg-surface-elevated border border-border text-xs text-muted-foreground hover:text-foreground shadow-sm transition-all hover:shadow-md">
          Scroll to bottom
        </button>
      </ThreadPrimitive.ScrollToBottom>
    </div>
  );
}
